<script lang="ts" module>
  /**
   * Die Bilder der leeren Flächen.
   *
   * ## Warum es sie gibt
   *
   * Ein neuer Nutzer sieht in einer frischen Reise zuerst elf leere Panels.
   * `Leerzustand.svelte` beantwortet dort seit jeher die Frage „was fehlt und
   * warum" — in Worten, und das ist die halbe Antwort. Die andere Hälfte ist
   * die Haltung: Ein gestrichelter Kasten mit zwei Zeilen Text sieht aus wie
   * ein Formular, das nicht geladen hat. Ein Bild darüber sagt in einem Blick,
   * dass hier etwas hinkommt.
   *
   * ## Der Strich
   *
   * Derselbe Gedanke wie beim Zeichensatz (`Zeichen.svelte`), eine Größe
   * weiter: 120×90, Kontur 2,5, runde Enden, `currentColor` — die Kontur ist
   * die Erwartung, sie steht in der Farbe des Textes ihrer Umgebung und
   * dunkelt mit ihm ein. Dazu **ein** Akzent in Terrakotta, und nur einer: die
   * Pfeilspitze am Weg, der Griff der Lupe, der Haken auf der Liste. Zwei
   * Akzente wären zwei Blickpunkte, und dann zeigte das Bild auf nichts.
   *
   * Emoji tun dieselbe Arbeit in einer Zeile Code und sehen auf jeder
   * Plattform anders aus — dickes Apple-Bunt neben schmalem Noto, und keines
   * davon gehört zu diesem Haus. Deshalb ein eigener Satz, gezeichnet wie der
   * Zeichensatz und mit ihm gealtert.
   *
   * ## Warum die Pfade hier stehen
   *
   * Inline statt als Datei unter `public/`: Das spart die Anfrage, umgeht jede
   * Frage nach der Content Security Policy und hält den Satz an einer Stelle.
   * Ein zweites Vorkommen liefe über kurz oder lang auseinander.
   *
   * Ein Bild ist **nie** die Auskunft. Es steht über einem Satz, der dasselbe
   * in Worten sagt, und ist deshalb für Vorlesegeräte ausgeblendet; `titel`
   * dient der Beschreibung im Quelltext und wird nur dann zum `aria-label`,
   * wenn das Bild ausdrücklich allein steht (`beschriftet`).
   */

  /** Ein Zug der Zeichnung. Vorgabe: Kontur, ungefüllt, 2,5 stark. */
  export interface Zug {
    d: string;
    /** Gefüllt statt gestrichen — für kleine Vollformen wie die Pfeilspitze. */
    gefuellt?: boolean;
    /**
     * Deckt den Untergrund ab, statt selbst zu erscheinen.
     *
     * Das Lupenglas: Ohne Deckung liefen die Linien der Karte hindurch, und
     * die Lupe sähe aus, als läge sie dahinter.
     */
    deckend?: boolean;
    gestrichelt?: boolean;
    /** Halb so laut — für Andeutungen (die Wellenlinie auf der Karte). */
    leise?: boolean;
    staerke?: number;
  }

  export interface Motiv {
    /** Was das Bild zeigt — im Quelltext, und als `aria-label`, wenn es allein steht. */
    titel: string;
    /** Die Konturzüge in `currentColor`. */
    kontur: Zug[];
    /** Der eine Terrakotta-Akzent. */
    akzent: Zug;
  }

  /**
   * Zehn Motive — drei für die Flächen, die es heute gibt, sieben auf Vorrat.
   *
   * Der Satz entsteht in einem Zug und nicht Bild für Bild bei Bedarf: Zehn
   * Zeichnungen an einem Nachmittag haben denselben Strich, zehn über drei
   * Monate verteilt haben zehn. Genau daran ist der Zeichensatz des Bestands
   * einmal fast gescheitert.
   *
   * Kreise und Rechtecke stehen als Pfade da und nicht als `<circle>`/`<rect>`:
   * So trägt jeder Zug dieselben Eigenschaften, und die Komponente braucht für
   * die Zeichnung genau eine Schleife statt einer Fallunterscheidung je Form.
   */
  export const MOTIVE = {
    /* Der Weg, der noch nicht gefahren ist: ein Standpunkt, eine gestrichelte
       Route, und die Spitze zeigt hinaus aus dem Bild. Dieselbe Grammatik wie
       auf der Karte — gestrichelt heißt kommend. */
    'reise-leer': {
      titel: 'Ein gestrichelter Weg, der aus dem Bild hinausführt',
      kontur: [
        /* Der Weg beginnt **am Rand** des Standpunkts und nicht in seiner
           Mitte: Ein Strich durch den Kreis sähe aus wie ein durchgestrichener
           Punkt, also nach Verbot statt nach Aufbruch. */
        { d: 'M17 73C42 60 34 34 62 26S104 18 112 8', gestrichelt: true },
        { d: 'M3 78a7 7 0 1 0 14 0 7 7 0 1 0-14 0' }
      ],
      akzent: { d: 'M104 2l8 6-8 6z', gefuellt: true }
    },

    /* Eine Karte, über der eine Lupe liegt: Gesucht wurde, gefunden nichts.
       Das Glas deckt ab, sonst läge die Karte darin. */
    'suche-leer': {
      titel: 'Eine Lupe über einer Karte',
      kontur: [
        { d: 'M20 16h58a6 6 0 0 1 6 6v40a6 6 0 0 1-6 6H20a6 6 0 0 1-6-6V22a6 6 0 0 1 6-6z' },
        { d: 'M14 44Q34 34 50 44T84 46', staerke: 2, leise: true },
        { d: 'M66 58a16 16 0 1 0 32 0 16 16 0 1 0-32 0', deckend: true }
      ],
      akzent: { d: 'M93 70L104 81', staerke: 3 }
    },

    /* Die Glocke steht für „jemand bekommt Nachricht davon" — dasselbe Bild
       wie in der App. Der Punkt daneben ist der, der noch niemandem gilt. */
    mitlesen: {
      titel: 'Eine Glocke, an der noch niemand hängt',
      kontur: [
        {
          d: 'M60 18C47 18 42 28 42 40v14l-6 8h48l-6-8V40c0-12-5-22-18-22z'
        },
        { d: 'M54 68a6 6 0 0 0 12 0' }
      ],
      akzent: { d: 'M83 24a5 5 0 1 0 10 0 5 5 0 1 0-10 0' }
    },

    /* Ein aufgeschlagenes Buch mit zwei angedeuteten Zeilen: Das Tagebuch
       liegt bereit, geschrieben wird unterwegs. Das Lesebändchen ist der
       Akzent — es markiert die Stelle, an der es weitergeht. */
    'tagebuch-wartet': {
      titel: 'Ein aufgeschlagenes Buch mit Lesebändchen',
      kontur: [
        { d: 'M26 20c12 0 24 2 34 8 10-6 22-8 34-8v46c-12 0-24 2-34 8-10-6-22-8-34-8z' },
        { d: 'M60 28v46' },
        { d: 'M36 38h16M36 48h12', staerke: 2, leise: true }
      ],
      akzent: { d: 'M78 22v22l5-5 5 5V21' }
    },

    /* Das Klemmbrett mit leeren Kästchen: Vorbereitung ist eine Liste, und
       eine Liste ohne Stationen hat nichts abzuhaken. Der eine Haken zeigt,
       was daraus wird. */
    'vorbereitung-leer': {
      titel: 'Ein Klemmbrett mit leeren Kästchen',
      kontur: [
        { d: 'M36 16h48a6 6 0 0 1 6 6v48a6 6 0 0 1-6 6H36a6 6 0 0 1-6-6V22a6 6 0 0 1 6-6z' },
        { d: 'M50 16v-3a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v3z' },
        { d: 'M40 36h11v11H40zM40 54h11v11H40z', staerke: 2 },
        { d: 'M57 41h23M57 59h17', staerke: 2, leise: true }
      ],
      akzent: { d: 'M42 41l3 3 6-7', staerke: 2.5 }
    },

    /* Der Papierkorb — mit dem Deckel, den man abnimmt, und drei Rillen. Die
       mittlere in Terrakotta: Was hier steht, ist noch da. */
    papierkorb: {
      titel: 'Ein Papierkorb mit drei Rillen',
      kontur: [
        { d: 'M40 30h40l-4 42a7 7 0 0 1-7 6H51a7 7 0 0 1-7-6z' },
        { d: 'M32 30h56' },
        { d: 'M50 30v-6a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v6' },
        { d: 'M53 44v22M67 44v22', staerke: 2 }
      ],
      akzent: { d: 'M60 44v22', staerke: 2.5 }
    },

    /* Eine volle Batterie: Der Ladeplan hat nichts zu tun, weil nichts fehlt.
       Der Blitz ist der Akzent und sagt, worum es ginge. */
    'laden-ohne-bedarf': {
      titel: 'Eine Batterie ohne Ladebedarf',
      kontur: [
        { d: 'M33 32h48a5 5 0 0 1 5 5v16a5 5 0 0 1-5 5H33a5 5 0 0 1-5-5V37a5 5 0 0 1 5-5z' },
        { d: 'M90 39h4v12h-4z', staerke: 2 },
        { d: 'M38 40v10M46 40v10', staerke: 2, leise: true }
      ],
      akzent: { d: 'M68 34l-9 13h6l-2 9 10-14h-6z', gefuellt: true }
    },

    /* Eine Straße, die zum Horizont läuft, und die Sonne darüber: der Anfang,
       bevor irgendetwas angelegt ist.

       Die beiden Ränder treffen sich ausdrücklich **nicht** in einem Punkt —
       das gäbe ein Dreieck, und ein Dreieck über einer Linie ist ein Berg
       oder ein Zelt. Zwanzig Pixel Abstand oben machen daraus eine Straße. */
    willkommen: {
      titel: 'Eine Straße, die zum Horizont führt',
      kontur: [
        { d: 'M12 76h96' },
        { d: 'M34 76l16-28M86 76l-16-28' },
        { d: 'M60 72v-7M60 60v-6', staerke: 2, leise: true }
      ],
      akzent: { d: 'M50 34a10 10 0 1 0 20 0 10 10 0 1 0-20 0' }
    },

    /* Die Funkwellen mit dem Strich darüber — dasselbe Bild wie `kein-netz`
       im Zeichensatz, nur groß. Der Strich ist der Akzent: Er ist die
       Aussage. */
    offline: {
      titel: 'Funkwellen mit einem Strich darüber',
      kontur: [
        { d: 'M30 42a42 42 0 0 1 60 0' },
        { d: 'M42 54a24 24 0 0 1 36 0' },
        { d: 'M53 66a10 10 0 0 1 14 0' },
        { d: 'M60 76h.01', staerke: 4 }
      ],
      akzent: { d: 'M28 24l64 54', staerke: 3 }
    },

    /* Ein leerer Kalender: Ein Tagesplan ist ein Tag, den jemand ausgearbeitet
       hat, und solange keiner hängt, sind es lauter gleiche Tage. Der Ring
       markiert den einen, um den es ginge. */
    'plan-wartet': {
      titel: 'Ein Kalender ohne Eintrag',
      kontur: [
        { d: 'M30 22h60a6 6 0 0 1 6 6v44a6 6 0 0 1-6 6H30a6 6 0 0 1-6-6V28a6 6 0 0 1 6-6z' },
        { d: 'M42 14v14M78 14v14' },
        { d: 'M24 38h72' },
        { d: 'M40 50h.01M56 50h.01M72 50h.01M40 64h.01M56 64h.01', staerke: 3.5, leise: true }
      ],
      akzent: { d: 'M65 64a7 7 0 1 0 14 0 7 7 0 1 0-14 0' }
    }
  } satisfies Record<string, Motiv>;

  export type Motivname = keyof typeof MOTIVE;
</script>

<script lang="ts">
  interface Props {
    name: Motivname;
    /** Breite in px; die Höhe folgt dem Seitenverhältnis 4:3. 96 im Leerzustand. */
    breite?: number;
    /**
     * Das Bild steht allein und trägt die Auskunft mit.
     *
     * Der Normalfall ist das Gegenteil: Es steht über einem Satz, der dasselbe
     * sagt, und wäre für ein Vorlesegerät eine zweite Ansage derselben Sache.
     */
    beschriftet?: boolean;
  }
  let { name, breite = 120, beschriftet = false }: Props = $props();

  const motiv = $derived(MOTIVE[name] as Motiv);

  /** Kontur, Deckfläche oder Vollform — die Füllung sagt, was der Zug ist. */
  function fuellung(z: Zug): string {
    if (z.deckend) return 'var(--bg)';
    return z.gefuellt ? 'currentColor' : 'none';
  }
</script>

<svg
  viewBox="0 0 120 90"
  width={breite}
  height={(breite * 3) / 4}
  fill="none"
  stroke-linecap="round"
  stroke-linejoin="round"
  role={beschriftet ? 'img' : 'presentation'}
  aria-label={beschriftet ? motiv.titel : undefined}
  aria-hidden={beschriftet ? undefined : 'true'}
>
  {#if beschriftet}<title>{motiv.titel}</title>{/if}
  {#each motiv.kontur as z}
    <path
      d={z.d}
      stroke="currentColor"
      stroke-width={z.staerke ?? 2.5}
      fill={fuellung(z)}
      stroke-dasharray={z.gestrichelt ? '6 7' : undefined}
      opacity={z.leise ? 0.5 : undefined}
    />
  {/each}
  <path
    d={motiv.akzent.d}
    stroke={motiv.akzent.gefuellt ? 'none' : 'var(--terrakotta)'}
    stroke-width={motiv.akzent.staerke ?? 2.5}
    fill={motiv.akzent.gefuellt ? 'var(--terrakotta)' : 'none'}
  />
</svg>

<style>
  /* `block`, damit unter dem Bild keine Grundlinienlücke steht — dieselbe
     Zeile wie im Zeichensatz.

     Die Kontur steht in der dritten Textstufe und nicht in der ersten: Ein
     Leerzustand ist eine ruhige Fläche, und ein Bild in Textschwarz wäre das
     Lauteste auf dem Panel. Gesetzt und nicht geerbt — es steht überall in
     derselben Lautstärke, und der Akzent ist der einzige Punkt, der heraussticht. */
  svg {
    display: block;
    flex: none;
    color: var(--text-schwach);
  }
</style>
